import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../../lib/styles/props/component-style-props";
import {
  Box,
  Checkbox,
  CheckboxGroup,
  ComponentMultiStyleConfig,
  Divider,
  Radio,
  RadioGroup,
  Text,
  useMultiStyleConfig,
  VStack,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { CheckboxContent, RadioContent } from "../../../control";
import { messageId, ValueObjectMetaEnum } from "../../../../lib";
import { useMessageDictionary } from "../../../../hooks/use-message-dictionary";
import { CMFrameButtonPopupSearchCondition } from "../CMFrameButtonPopupSearchCondition/CMFrameButtonPopupSearchCondition";
import { hasValue } from "../../../../lib/util/common-util";

export type CMButtonPopupSearchConditionCheckboxAndRadioProps<
  C extends [string, ...string[]],
  R extends [string, ...string[]],
> = {
  /**
   * 値オブジェクトメタデータ
   */
  valueObjectMetaCheckbox: ValueObjectMetaEnum<C>;

  /**
   * チェックボックスの値
   */
  checkboxInternalValues: C[number][];

  /**
   * 値オブジェクトメタデータ
   */
  valueObjectMetaRadio: ValueObjectMetaEnum<R>;

  /**
   * ラジオボタンの値
   */
  radioInternalValue?: R[number] | null;

  labelSubmit?: string;
  labelClear?: string;

  /**
   * クリアボタンの表示制御 trueの場合にクリアボタン非表示
   */
  hideClearButton?: boolean;

  /**
   * ラジオボタンとチェックボックスで選択された値を呼び出し元に返却
   */
  onChangeInternalValues: (selectedValue: {
    checkbox: C[number][];
    radio: R[number] | null;
  }) => void;

  onSubmit: () => void;
  onClear: () => void;
} & ComponentStyleProps;

/**
 * ラジオボタン検索条件のコンポーネント.
 *
 */
export const CMButtonPopupSearchConditionCheckboxAndRadio = <
  C extends [string, ...string[]],
  R extends [string, ...string[]],
>({
  valueObjectMetaCheckbox,
  checkboxInternalValues,

  valueObjectMetaRadio,
  radioInternalValue = null,

  labelSubmit,
  labelClear,
  hideClearButton = false,

  onChangeInternalValues,

  onSubmit,
  onClear,

  sx,

  ...rest
}: CMButtonPopupSearchConditionCheckboxAndRadioProps<C, R>) => {
  const styles = useMultiStyleConfig(
    "CMButtonPopupSearchConditionCheckboxAndRadioStyles",
  );
  const dict = useMessageDictionary();

  /**
   * チェックボックスの入力値を取得し、保持。
   */
  const handleChangeCheckboxInternalValues = (
    changedValues: (string | number)[],
  ) => {
    const _changedValues = changedValues
      .map((v) => valueObjectMetaCheckbox.toDomainObjectOrNull(v.toString()))
      .filter(hasValue);

    onChangeInternalValues({
      checkbox: _changedValues,
      radio: radioInternalValue,
    });
  };

  /**
   * ラジオボタンの入力値を取得し、保持。
   */
  const handleChangeRadioInternalValue = (changedValue: string | null) => {
    onChangeInternalValues({
      checkbox: checkboxInternalValues,
      radio: valueObjectMetaRadio.toDomainObjectOrNull(changedValue),
    });
  };

  // const [checkboxInternalValues, setCheckboxInternalValues] = useState<
  //   C[number][]
  // >(checkboxInternalValues ?? []);
  // useEffect(() => {
  //   setCheckboxInternalValues(checkboxInternalValues ?? []);
  // }, [checkboxInternalValues]);

  // metaデータから取得した値をcontentの形に変換する
  const checkboxContent: CheckboxContent = useMemo(
    () => ({
      options: valueObjectMetaCheckbox.values.map((v) => ({
        label:
          dict(messageId(valueObjectMetaCheckbox.dict[v as C[number]])) ?? "",
        value: v,
      })),
    }),
    [valueObjectMetaCheckbox, dict],
  );

  // const radioDefaultValue: R[number] = valueObjectMetaRadio.values[0];
  // const [radioInternalValue, setRadioInternalValue] = useState<R[number]>(
  //   radioInternalValue ?? radioDefaultValue,
  // );
  // useEffect(() => {
  //   setRadioInternalValue(radioInternalValue ?? radioDefaultValue);
  // }, [radioInternalValue, radioDefaultValue]);

  // metaデータから取得した値をcontentの形に変換する
  const radioContent: RadioContent = useMemo(
    () => ({
      options: valueObjectMetaRadio.values.map((v) => ({
        label: dict(messageId(valueObjectMetaRadio.dict[v as R[number]])) ?? "",
        value: v,
      })),
    }),
    [valueObjectMetaRadio, dict],
  );

  // /**
  //  * Submitボタン押下時、呼び出し元に選択値を返却
  //  */
  // const handleSubmit = useCallback(() => {
  //   const checkboxSubmitValue =
  //     checkboxInternalValues.length > 0 ? checkboxInternalValues : null;
  //   onChange?.({ radio: radioInternalValue, checkbox: checkboxSubmitValue });
  // }, [onChange, radioInternalValue, checkboxInternalValues]);
  //
  // /**
  //  * Clearボタン押下時、呼び出し元にnullを返却
  //  */
  // const handleClear = useCallback(() => {
  //   setRadioInternalValue(radioDefaultValue);
  //   setCheckboxInternalValues([]);
  //   onChange?.(null);
  // }, [onChange, radioDefaultValue]);
  //
  // /**
  //  * チェックボックスの入力値を取得し、保持。
  //  * @param changedValues 選択値
  //  */
  // const handleChangeCheckbox = (changedValues: (string | number)[]) => {
  //   const _changedValues = changedValues
  //     .map((v) => {
  //       const validated = valueObjectMetaCheckbox.validate(v.toString());
  //       if (validated.state !== "ok") return null;
  //       return validated.value;
  //     })
  //     .filter(hasValue);
  //   setCheckboxInternalValues(_changedValues);
  // };
  //
  // /**
  //  * ラジオボタンの入力値を取得し、保持。
  //  * @param changedValue 選択値
  //  */
  // const handleChangeRadio = (changedValue: string | null) => {
  //   const validated = valueObjectMetaRadio.validate(changedValue);
  //   if (validated.state === "ok") {
  //     setRadioInternalValue(validated.value);
  //   } else {
  //     setRadioInternalValue(radioDefaultValue);
  //   }
  // };

  return (
    <CMFrameButtonPopupSearchCondition
      labelSubmit={labelSubmit}
      labelClear={labelClear}
      hideClearButton={hideClearButton}
      onSubmit={onSubmit}
      onClear={onClear}
      {...rest}
      sx={{ ...styles.container, ...sx }}
    >
      <CheckboxGroup
        defaultValue={checkboxInternalValues ?? undefined}
        value={checkboxInternalValues ?? undefined}
        onChange={handleChangeCheckboxInternalValues}
      >
        <VStack sx={styles.checkboxStack}>
          {checkboxContent.options.map((option, index) => (
            <Checkbox key={index} value={option.value} sx={styles.checkbox}>
              <Text sx={styles.checkboxLabel}>{option.label}</Text>
            </Checkbox>
          ))}
        </VStack>
      </CheckboxGroup>
      <Divider orientation={"horizontal"} sx={styles.divider} />
      <RadioGroup
        defaultValue={radioInternalValue ?? undefined}
        value={radioInternalValue ?? undefined}
        onChange={handleChangeRadioInternalValue}
      >
        <VStack sx={styles.radioStack}>
          {radioContent.options.map((option, index) => (
            <Box key={index}>
              <Radio key={index} value={option.value} sx={styles.radio}>
                <Text sx={styles.radioLabel}>{option.label}</Text>
              </Radio>
            </Box>
          ))}
        </VStack>
      </RadioGroup>
    </CMFrameButtonPopupSearchCondition>
  );
};

export const CMButtonPopupSearchConditionCheckboxAndRadioStyles: ComponentMultiStyleConfig =
  {
    parts: ["container", "radioInput", "divider", "checkboxInput"],
    baseStyle: {
      container: {},
      radioInput: {},
      divider: { border: "solid 1px", borderColor: "gray.200", my: 3 },
      checkboxInput: {},
    },
  };
